import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Plus, Share2, Copy, ExternalLink, Trash2, 
  CheckCircle, XCircle, Clock, AlertCircle
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';

const InventoryShareLinks = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productRes, linksRes] = await Promise.all([
        inventoryAPI.getProduct(productId),
        inventoryAPI.listShareLinks(productId)
      ]);
      setProduct(productRes.data);
      setShareLinks(linksRes.data.share_links);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/partners/inventory');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const handleRevokeLink = async (token) => {
    if (!window.confirm('Revoke this share link? Customers will no longer be able to use it.')) {
      return;
    }

    try {
      await inventoryAPI.revokeShareLink(productId, token);
      toast.success('Link revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const getStatusBadge = (link) => {
    if (link.status === 'REVOKED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3 h-3" />
          Revoked
        </span>
      );
    }
    
    // Check if expired
    if (link.expires_at && new Date(link.expires_at) < new Date()) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          <Clock className="w-3 h-3" />
          Expired
        </span>
      );
    }
    
    // Check max uses
    if (link.max_uses && link.used_count >= link.max_uses) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <AlertCircle className="w-3 h-3" />
          Max Uses Reached
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const canCreateLink = product?.tryon_input_image_key;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/partners/inventory/${productId}`}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Share Links</h1>
          <p className="text-slate-500">{product?.product_name} ({product?.sku_id})</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={!canCreateLink}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          Create Link
        </button>
      </div>

      {/* Warning if no tryon image */}
      {!canCreateLink && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Cannot create share links</p>
            <p className="text-sm text-amber-700">
              Please upload and select a Try-On input image first.
            </p>
            <Link
              to={`/partners/inventory/${productId}/images`}
              className="text-sm text-amber-800 underline hover:no-underline mt-1 inline-block"
            >
              Go to Images →
            </Link>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">🔗 Share this link with your customer</p>
        <p>The dress is preloaded. Customers only upload their photo to try on the look.</p>
      </div>

      {/* Share Links List */}
      {shareLinks.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-12 text-center"
        >
          <Share2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No share links yet</h3>
          <p className="text-slate-500 mb-6">
            Create shareable links to let customers try on this product
          </p>
          {canCreateLink && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Create First Link
            </button>
          )}
        </motion.div>
      ) : (
        <div className="space-y-4">
          {shareLinks.map((link) => (
            <motion.div
              key={link.token}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 p-4"
            >
              <div className="flex items-start gap-4">
                {/* Preview */}
                {link.tryon_input_image_url && (
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                    <img
                      src={link.tryon_input_image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(link)}
                    <span className="text-xs text-slate-400">
                      Created {new Date(link.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* URL */}
                  <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 mb-2">
                    <code className="flex-1 text-sm text-slate-600 truncate">
                      {link.share_url}
                    </code>
                    <button
                      onClick={() => handleCopyLink(link.share_url)}
                      className="p-1 text-slate-400 hover:text-blue-600"
                      title="Copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={link.share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-slate-400 hover:text-blue-600"
                      title="Open"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  {/* Stats */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Usage Progress Badge */}
                    {(() => {
                      const usedCount = link.used_count || 0;
                      const maxUses = link.max_uses;
                      const percentage = maxUses ? (usedCount / maxUses) * 100 : 0;
                      const statusColor = !maxUses 
                        ? 'bg-slate-100 text-slate-600' 
                        : percentage >= 90 
                          ? 'bg-red-100 text-red-700' 
                          : percentage >= 70 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-emerald-100 text-emerald-700';
                      
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                          {maxUses ? `${usedCount}/${maxUses} used` : `${usedCount} uses`}
                        </span>
                      );
                    })()}
                    
                    {/* Remaining Days Badge */}
                    {link.expires_at && (() => {
                      const now = new Date();
                      const expiry = new Date(link.expires_at);
                      const diffTime = expiry - now;
                      const remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      
                      const statusColor = remainingDays <= 0 
                        ? 'bg-red-100 text-red-700'
                        : remainingDays <= 3 
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600';
                      
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                          <Clock className="w-3 h-3" />
                          {remainingDays <= 0 
                            ? 'Expired' 
                            : remainingDays === 1 
                              ? '1 day left' 
                              : `${remainingDays} days left`}
                        </span>
                      );
                    })()}
                    
                    {/* Per-User Limit Badge */}
                    {link.max_uses_per_user && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        Max {link.max_uses_per_user}/user
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {link.status === 'ACTIVE' && (
                  <button
                    onClick={() => handleRevokeLink(link.token)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Revoke"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <Link
          to={`/partners/inventory/${productId}/variants`}
          className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
        >
          ← Manage Variants
        </Link>
        <Link
          to={`/partners/inventory/${productId}`}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Product
        </Link>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateShareLinkModal
          productId={productId}
          productName={product?.product_name}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Create Share Link Modal
const CreateShareLinkModal = ({ productId, productName, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryDays, setExpiryDays] = useState(7);
  const [useMaxUses, setUseMaxUses] = useState(true);  // Default ON for safety
  const [maxUses, setMaxUses] = useState(10);  // Default to 10 uses
  const [useMaxUsesPerUser, setUseMaxUsesPerUser] = useState(true);  // Default ON
  const [maxUsesPerUser, setMaxUsesPerUser] = useState(1);  // Default 1 per user
  const [buyNowLink, setBuyNowLink] = useState('');
  const [createdLink, setCreatedLink] = useState(null);

  const handleCreate = async () => {
    try {
      setLoading(true);
      
      const data = {};
      
      if (useExpiry) {
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + expiryDays);
        data.expires_at = expiresAt.toISOString();
      }
      
      if (useMaxUses) {
        data.max_uses = maxUses;
      }
      
      if (useMaxUsesPerUser) {
        data.max_uses_per_user = maxUsesPerUser;
      }
      
      if (buyNowLink.trim()) {
        data.buy_now_link = buyNowLink.trim();
      }

      const response = await inventoryAPI.createShareLink(productId, data);
      setCreatedLink(response.data);
      toast.success('Share link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(createdLink.share_url);
    toast.success('Link copied!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-md w-full p-6"
      >
        {createdLink ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Link Created!</h2>
              <p className="text-slate-500 mt-1">Share this link with your customer</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500 mb-2">Share URL:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-slate-800 break-all">
                  {createdLink.share_url}
                </code>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onCreated}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Done
              </button>
              <button
                onClick={handleCopy}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy Link
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Create Share Link</h2>
            <p className="text-slate-500 mb-6">for {productName}</p>

            <div className="space-y-4">
              {/* Expiry Option */}
              <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={useExpiry}
                  onChange={(e) => setUseExpiry(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Set expiry date</p>
                  <p className="text-sm text-slate-500">Link will stop working after specified days</p>
                  {useExpiry && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        value={expiryDays}
                        onChange={(e) => setExpiryDays(parseInt(e.target.value) || 1)}
                        min="1"
                        max="365"
                        className="w-20 px-2 py-1 border border-slate-200 rounded"
                      />
                      <span className="text-sm text-slate-600">days</span>
                    </div>
                  )}
                </div>
              </label>

              {/* Max Uses Option */}
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${useMaxUses ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input
                  type="checkbox"
                  checked={useMaxUses}
                  onChange={(e) => setUseMaxUses(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Limit total uses {useMaxUses && <span className="text-blue-600 text-sm ml-1">✓ Enabled</span>}</p>
                  <p className="text-sm text-slate-500">Link will stop working after specified uses</p>
                  {useMaxUses && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        value={maxUses}
                        onChange={(e) => setMaxUses(parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-20 px-2 py-1 border border-blue-300 rounded bg-white"
                      />
                      <span className="text-sm text-slate-600">total uses</span>
                    </div>
                  )}
                  {!useMaxUses && (
                    <p className="mt-1 text-xs text-amber-600">⚠️ No limit - unlimited uses allowed</p>
                  )}
                </div>
              </label>

              {/* Per-User Limit Option */}
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${useMaxUsesPerUser ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input
                  type="checkbox"
                  checked={useMaxUsesPerUser}
                  onChange={(e) => setUseMaxUsesPerUser(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">Limit per customer {useMaxUsesPerUser && <span className="text-blue-600 text-sm ml-1">✓ Enabled</span>}</p>
                  <p className="text-sm text-slate-500">Max try-ons allowed per individual user</p>
                  {useMaxUsesPerUser && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        value={maxUsesPerUser}
                        onChange={(e) => setMaxUsesPerUser(parseInt(e.target.value) || 1)}
                        min="1"
                        max="100"
                        className="w-20 px-2 py-1 border border-blue-300 rounded bg-white"
                      />
                      <span className="text-sm text-slate-600">per user</span>
                    </div>
                  )}
                  {!useMaxUsesPerUser && (
                    <p className="mt-1 text-xs text-amber-600">⚠️ No limit - same user can try unlimited times</p>
                  )}
                </div>
              </label>

              {/* Buy Now Link Option */}
              <div className="p-3 border border-slate-200 rounded-lg">
                <p className="font-medium text-slate-900 mb-1">Buy Now Link (Optional)</p>
                <p className="text-sm text-slate-500 mb-2">
                  Where should customers go to purchase? Leave empty to capture interest via email.
                </p>
                <input
                  type="url"
                  value={buyNowLink}
                  onChange={(e) => setBuyNowLink(e.target.value)}
                  placeholder="https://your-store.com/product"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Link'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default InventoryShareLinks;
